import type { BoggleRunSummaryScore } from '~/boggle/config/runSummaryScore'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import type { BoggleGameModeId, BoggleRunKind } from '~/boggle/gameModes'
import type { BoggleCompletedRunProgressSummary } from '~/boggle/progress/progressRepository'
import type { BoggleRunAttemptIdentity } from '~/boggle/progress/runCompletion'
import {
  addTelemetryBreadcrumb,
  captureProductTelemetry,
  captureTelemetryException,
  setTelemetryTags,
} from '~/telemetry/appTelemetry'
import {
  buildBoggleRunAbandonedTelemetryProperties,
  buildBoggleRunCompletedTelemetryProperties,
  buildBoggleRunStartedTelemetryProperties,
  type BoggleRunTelemetryProperties,
  type BoggleRunTelemetryReason,
} from './boggleTelemetryPayloads'

export type BoggleTelemetryEventName =
  | 'boggle_challenge_revealed'
  | 'boggle_mode_changed'
  | 'boggle_run_abandoned'
  | 'boggle_run_completed'
  | 'boggle_run_started'

export type BoggleTelemetryEvent = {
  name: BoggleTelemetryEventName
  properties: BoggleRunTelemetryProperties
}

export function captureBoggleTelemetry(event: BoggleTelemetryEvent) {
  captureProductTelemetry(event.name, event.properties)
  addTelemetryBreadcrumb({
    category: 'boggle',
    data: event.properties,
    message: event.name,
  })
}

export function captureBoggleRunStarted(input: {
  gameModeId: BoggleGameModeId
  runAttempt: BoggleRunAttemptIdentity
  runKind: BoggleRunKind
}) {
  const properties = buildBoggleRunStartedTelemetryProperties(input)
  captureBoggleTelemetry({ name: 'boggle_run_started', properties })
  setTelemetryTags({
    boggleGameModeId: input.gameModeId,
    boggleRunId: input.runAttempt.id,
    boggleRunKind: input.runKind,
    boggleRunPhase: 'playing',
  })
}

export function captureBoggleRunCompleted(input: {
  gameModeId: BoggleGameModeId
  runAttempt: BoggleRunAttemptIdentity
  runKind: BoggleRunKind
  state: Pick<
    BoggleGameState,
    | 'cols'
    | 'endReason'
    | 'phase'
    | 'runLedger'
    | 'selection'
    | 'timeMs'
    | 'tilesCleared'
  >
  progressSummary?: BoggleCompletedRunProgressSummary
  summaryScore: BoggleRunSummaryScore
}) {
  const properties = buildBoggleRunCompletedTelemetryProperties(input)
  captureBoggleTelemetry({ name: 'boggle_run_completed', properties })
  setTelemetryTags({
    boggleRatingId: input.summaryScore.rating.id,
    boggleRunPhase: 'ended',
    boggleScore: input.summaryScore.total,
  })
}

export function captureBoggleRunAbandoned(input: {
  gameModeId: BoggleGameModeId
  reason: BoggleRunTelemetryReason
  runAttempt: BoggleRunAttemptIdentity
  runKind: BoggleRunKind
  state: Pick<
    BoggleGameState,
    | 'cols'
    | 'endReason'
    | 'phase'
    | 'runLedger'
    | 'selection'
    | 'timeMs'
    | 'tilesCleared'
  >
}) {
  if (input.state.phase !== 'playing') {
    return
  }

  const properties = buildBoggleRunAbandonedTelemetryProperties(input)
  captureBoggleTelemetry({ name: 'boggle_run_abandoned', properties })
}

export function captureBoggleModeChanged(properties: {
  gameModeId: BoggleGameModeId
  previousGameModeId: BoggleGameModeId | null
}) {
  captureBoggleTelemetry({
    name: 'boggle_mode_changed',
    properties: {
      eventSchemaVersion: 1,
      gameModeId: properties.gameModeId,
      previousGameModeId: properties.previousGameModeId,
    },
  })
}

export function captureBoggleChallengeRevealed(properties: {
  aceScore: number
  challengeConfigurationId: string | null
  challengeProgressSeed: number | string
  gameModeId: BoggleGameModeId
  targetScore: number
}) {
  captureBoggleTelemetry({
    name: 'boggle_challenge_revealed',
    properties: {
      aceScore: properties.aceScore,
      challengeConfigurationId: properties.challengeConfigurationId ?? undefined,
      challengeProgressSeed: String(properties.challengeProgressSeed),
      eventSchemaVersion: 1,
      gameModeId: properties.gameModeId,
      targetScore: properties.targetScore,
    },
  })
}

export function captureBoggleException(error: unknown, context: BoggleRunTelemetryProperties) {
  captureTelemetryException(error, {
    feature: 'boggle',
    ...context,
  })
}
